import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  CardImg,
  Spinner,
  Button,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  UncontrolledTooltip,
} from "reactstrap";
import { getProjectSummary } from "../project/service";
import { downloadZip, getAllRoomRenderImages } from "./roomRendersService";
import { CustomizationSpinnerContainer } from "../common/style";
import { useParams } from "react-router-dom";
import AutoCaptureButton from "./AutoCaptureButton";
import CapturedImages from "./CapturedImages";

const RoomRendersPane = ({
  projectSummary,
  currentRoom,
  loadRoom,
  canvasFunctions,
  canvasContainerRef,
  allRooms,
  onCaptureComplete,
  onLoadingStateChange = null, // Callback to notify parent of loading state
}) => {
  // Helper function to add timestamp parameter to image URLs to avoid cache
  const addTimestampToUrl = (url) => {
    if (!url) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_t=${Date.now()}`;
  };

  // Helper function to format image type for display
  const formatImageType = (imageType) => {
    if (!imageType) return "Image";
    
    // Extract base type and view suffix
    // Format: "external_with_label_a_1w" -> "External A (1W)"
    const viewMatch = imageType.match(/_([a-z])_1w$/);
    const baseType = imageType.replace(/_([a-z])_1w$/, "");
    
    // Map base types to display names
    const baseTypeNames = {
      "external_with_label": "External",
      "internal_with_height_and_width": "Internal",
      "room_name_with_internal": "Internal",
      "room_name_with_external": "External",
      "room_name_with_depth": "Depth",
      "room_name_with_internal_dimension": "Internal Dimension",
      "room_name_with_external_dimension": "External Dimension",
      "room_name_with_shutter_dimension": "Shutter Dimension",
    };
    
    // Map view suffix to label
    const suffixToLabel = { a: "A", b: "B", c: "C", d: "D", t: "T" };
    
    const displayName = baseTypeNames[baseType] || baseType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    
    if (viewMatch && viewMatch[1]) {
      const suffix = viewMatch[1];
      const viewLabel = suffixToLabel[suffix] || suffix.toUpperCase();
      return `${displayName} ${viewLabel} (1W)`;
    }
    
    return displayName;
  };
  const params = useParams();
  const projectId = params.projectId;
  const [rooms, setRooms] = useState([]);
  const [roomImages, setRoomImages] = useState({}); // Map room ID to images array
  const [loading, setLoading] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false); // Track image loading state
  const [openAccordion, setOpenAccordion] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const captureButtonRef = useRef(null);
  const [canvasFunctionsReady, setCanvasFunctionsReady] = useState(false);
  const [waitingForFunctions, setWaitingForFunctions] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false); // Track if overlay is showing to keep component mounted


  // Check if canvas functions are available (they come from RoomCanvas)
  useEffect(() => {
    if (canvasFunctions && canvasFunctions.configureCanvasForImageType && canvasFunctions.forceUpdate) {
      setCanvasFunctionsReady(true);
      setWaitingForFunctions(false);
    } else {
      setCanvasFunctionsReady(false);
      setWaitingForFunctions(true);
    }
  }, [canvasFunctions]);

  // Load rooms when component mounts or projectSummary changes
  useEffect(() => {
    loadRooms();
  }, [projectSummary]);

  // Load room images - check on mount to see if images exist, then load lazily for details
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const loadRoomImagesIfNeeded = () => {
    if (!imagesLoaded && projectId) {
      loadRoomImages();
      setImagesLoaded(true);
    }
  };

  // Load images on mount to check if any exist (for showing/hiding empty state)
  // This is a lightweight check - we still load full details lazily when needed
  useEffect(() => {
    if (projectId && !imagesLoaded) {
      loadRoomImages();
      setImagesLoaded(true);
    }
  }, [projectId]);

  // Also load images when accordion is opened (to refresh/ensure latest data)
  useEffect(() => {
    if (openAccordion !== null) {
      loadRoomImagesIfNeeded();
    }
  }, [openAccordion]);

  const loadRooms = async () => {
    setLoading(true);
    try {
      let roomsData = [];
      if (projectSummary?.rooms) {
        roomsData = projectSummary.rooms;
      } else {
        const summary = await getProjectSummary(projectId);
        roomsData = summary.rooms || [];
      }
      setRooms(roomsData);
    } catch (error) {
      console.error("Failed to load rooms:", error);
    } finally {
      setLoading(false);
    }
  };

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(loadingImages);
    }
  }, [loadingImages, onLoadingStateChange]);

  const loadRoomImages = async () => {
    if (!projectId) {
      return;
    }

    setLoadingImages(true);
    try {
      const imagesData = await getAllRoomRenderImages(projectId);
      // Normalize response: ensure room IDs are strings to match room.id.toString() usage
      // Expected format: { "1": [...], "2": [...], "3": [] }
      const normalizedImages = {};
      if (imagesData && typeof imagesData === 'object') {
        Object.keys(imagesData).forEach(roomId => {
          // Convert room ID to string and ensure value is an array
          const roomIdStr = String(roomId);
          normalizedImages[roomIdStr] = Array.isArray(imagesData[roomId])
            ? imagesData[roomId]
            : [];
        });
      }
      setRoomImages(normalizedImages);
    } catch (error) {
      // If API doesn't exist yet, use empty object
      setRoomImages({});
    } finally {
      setLoadingImages(false);
    }
  };

  const handleCapture = async () => {
    try {
      let functionRetries = 0;
      const maxFunctionRetries = 100; // 100 * 100ms = 10 seconds

      while (functionRetries < maxFunctionRetries) {
        if (canvasFunctions && canvasFunctions.configureCanvasForImageType && canvasFunctions.forceUpdate) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        functionRetries++;
      }

      // Wait for AutoCaptureButton to be rendered and ready (up to 10 seconds)
      let buttonRetries = 0;
      const maxButtonRetries = 100; // 100 * 100ms = 10 seconds

      while (buttonRetries < maxButtonRetries) {
        if (captureButtonRef.current && captureButtonRef.current.startCapture) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        buttonRetries++;
      }

      // Check if AutoCaptureButton is available
      if (!captureButtonRef.current || !captureButtonRef.current.startCapture) {
        let errorMsg = "Capture functionality not available.\n\n";
        if (!projectId) errorMsg += "- Project ID missing\n";
        if (!currentRoom) errorMsg += "- Current room not set\n";
        if (!canvasFunctionsReady) errorMsg += "- Canvas functions not ready\n";
        if (!canvasFunctions) errorMsg += "- Canvas functions not provided\n";
        if (!canvasFunctions?.configureCanvasForImageType) errorMsg += "- configureCanvasForImageType missing\n";
        if (!canvasFunctions?.forceUpdate) errorMsg += "- forceUpdate missing\n";

        alert(errorMsg + "\nPlease ensure you're in room view and try again.");
        return;
      }

      // Trigger capture through AutoCaptureButton
      // AutoCaptureButton.handleStartCapture will:
      // - Enable room view if needed
      // - Wait for canvas functions
      // - Capture all rooms with automatic toggle configuration
      // Images will be reloaded automatically via onCaptureSuccess callback
      await captureButtonRef.current.startCapture(true); // Capture all rooms
    } catch (error) {
      console.error("Capture failed:", error);
      const errorMessage = error?.error || error?.message || error?.toString() || "Unknown error";
      alert("Capture failed: " + errorMessage);
    }
  };

  const handleDownload = async () => {
    if (!projectId || !currentRoom) {
      alert("Project or room information not available");
      return;
    }

    setDownloading(true);
    try {
      // Call the room-specific download endpoint: /api/projects/:projectId/rooms/:roomId/room_render/download_zip
      await downloadZip(projectId, currentRoom.id);
    } catch (error) {
      console.error("Failed to download:", error);
      alert(error.message || "Failed to download ZIP file");
    } finally {
      setDownloading(false);
    }
  };

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const getRoomRenderImages = (room) => {
    return roomImages[room.id] || room.room_render_images || [];
  };

  if (loading) {
    return (
      <CustomizationSpinnerContainer top="200px">
        <Spinner />
      </CustomizationSpinnerContainer>
    );
  }

  // Debug: Log render conditions
  const canRenderButton = projectId && currentRoom && canvasFunctionsReady && canvasFunctions && canvasFunctions.configureCanvasForImageType && canvasFunctions.forceUpdate;

  // Load images when user hovers over download/print buttons (indicates they want to see images)
  const handleDownloadHover = () => {
    loadRoomImagesIfNeeded();
  };

  // Check if there are any captured images
  const hasCapturedImages = Object.keys(roomImages).some(
    (roomId) => roomImages[roomId] && roomImages[roomId].length > 0
  );

  // Show default message if no images exist
  // Only show if: not loading, images have been checked, and no images exist
  const shouldShowEmptyState = !loading && !loadingImages && imagesLoaded && !hasCapturedImages;

  if (shouldShowEmptyState) {
    return (
      <div className="p-3">
        {/* Hidden AutoCaptureButton to trigger capture */}
        {/* Keep mounted if overlay is visible to prevent state loss */}
        {(canRenderButton || overlayVisible) && (
          <div style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}>
            <AutoCaptureButton
              ref={captureButtonRef}
              projectId={projectId}
              roomId={currentRoom?.id}
              canvasContainerRef={canvasContainerRef}
              configureCanvasForImageType={canvasFunctions?.configureCanvasForImageType}
              forceUpdate={canvasFunctions?.forceUpdate}
              onCaptureComplete={onCaptureComplete || canvasFunctions?.restoreOriginalState}
              onCaptureSuccess={() => {
                // Reload images and rooms after successful capture
                loadRoomImages();
                setImagesLoaded(true);
                loadRooms();
                setOverlayVisible(false);
              }}
              hasRoomRenderImages={currentRoom?.has_room_render_images || false}
              allRooms={allRooms}
              loadRoom={loadRoom}
              currentRoom={currentRoom}
              onOverlayVisibilityChange={setOverlayVisible}
              onCaptureStatusChange={(isCapturing) => {
                // Store capture status in canvasFunctions so NewPersonalize can access it
                if (canvasFunctions) {
                  canvasFunctions.isCapturing = isCapturing;
                }
              }}
            />
          </div>
        )}

        <div className="text-center py-5">
          <i className="fa-solid fa-image fa-3x text-muted mb-3" style={{ opacity: 0.3 }} />
          <p className="text-muted mb-3" style={{ fontSize: "1.1rem" }}>
            Drawings not generated yet
          </p>
          <Button
            color="primary"
            onClick={handleCapture}
            disabled={!canvasFunctionsReady || !canvasFunctions}
            title={!canvasFunctionsReady || !canvasFunctions ? "Waiting for canvas to initialize..." : "Generate drawings"}
          >
            <i className="fa-solid fa-camera me-2" />
            {waitingForFunctions ? "Initializing..." : "Generate"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
        {/* Hidden AutoCaptureButton to trigger capture */}
        {/* Keep mounted if overlay is visible to prevent state loss */}
        {(canRenderButton || overlayVisible) && (
          <div style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}>
            <AutoCaptureButton
              ref={captureButtonRef}
              projectId={projectId}
              roomId={currentRoom?.id}
              canvasContainerRef={canvasContainerRef}
              configureCanvasForImageType={canvasFunctions?.configureCanvasForImageType}
              forceUpdate={canvasFunctions?.forceUpdate}
              onCaptureComplete={onCaptureComplete || canvasFunctions?.restoreOriginalState}
              onCaptureSuccess={() => {
                // Reload images and rooms after successful capture
                loadRoomImages();
                setImagesLoaded(true);
                loadRooms();
                setOverlayVisible(false);
              }}
              hasRoomRenderImages={currentRoom?.has_room_render_images || false}
              allRooms={allRooms}
              loadRoom={loadRoom}
              currentRoom={currentRoom}
              onOverlayVisibilityChange={setOverlayVisible}
              onCaptureStatusChange={(isCapturing) => {
                // Store capture status in canvasFunctions so NewPersonalize can access it
                if (canvasFunctions) {
                  canvasFunctions.isCapturing = isCapturing;
                }
              }}
            />
          </div>
        )}

      {/* Header with Capture button and action icons */}
      <div className="d-flex gap-2 mb-3 align-items-center justify-content-between">
        <Button
          color="primary"
          onClick={handleCapture}
          className="flex-shrink-0"
          style={{ minWidth: "130px" }}
          disabled={!canvasFunctionsReady || !canvasFunctions}
          title={!canvasFunctionsReady || !canvasFunctions ? "Waiting for canvas to initialize..." : hasCapturedImages ? "Re-generate all rooms" : "Capture all rooms"}
        >
          <i className="fa-solid fa-camera me-2" />
          {waitingForFunctions ? "Initializing..." : hasCapturedImages ? "Re-generate" : "Capture"}
        </Button>
        <div className="d-flex align-items-center gap-3">
          {loadingImages && (
            <Spinner size="sm" className="me-2" />
          )}
          {downloading ? (
            <Spinner size="sm" style={{ fontSize: "3rem" }} />
          ) : (
            <>
              <i
                id="download-icon"
                className={`fa-solid fa-download p-2 ${hasCapturedImages ? "text-primary" : "text-secondary"} ${hasCapturedImages ? "" : "opacity-50"}`}
                style={{
                  fontSize: "1.25rem",
                  cursor: hasCapturedImages ? "pointer" : "not-allowed",
                }}
                onClick={hasCapturedImages ? handleDownload : undefined}
                onMouseEnter={handleDownloadHover}
              />
              {!hasCapturedImages && (
                <UncontrolledTooltip target="download-icon" placement="top">
                  No captured images to download
                </UncontrolledTooltip>
              )}
              <i
                id="print-icon"
                className={`fa-solid fa-print p-2 ${hasCapturedImages ? "text-primary" : "text-secondary"} ${hasCapturedImages ? "" : "opacity-50"}`}
                style={{
                  fontSize: "1.25rem",
                  cursor: hasCapturedImages ? "pointer" : "not-allowed",
                }}
                onClick={hasCapturedImages ? () => {
                  loadRoomImagesIfNeeded();
                  setShowPrintModal(true);
                } : undefined}
                onMouseEnter={handleDownloadHover}
              />
              {!hasCapturedImages && (
                <UncontrolledTooltip target="print-icon" placement="top">
                  No captured images to print
                </UncontrolledTooltip>
              )}
            </>
          )}
        </div>
      </div>

      {/* Rooms Accordion */}
      <Accordion open={openAccordion || ""} toggle={toggleAccordion}>
        {rooms.map((room) => {
          const images = getRoomRenderImages(room);
          const hasImages = images && images.length > 0;
          const roomIdStr = room.id.toString();

          return (
            <AccordionItem key={room.id}>
              <AccordionHeader targetId={roomIdStr}>
                <div className="d-flex justify-content-between align-items-center w-100 me-3">
                  <span>{room.name || `Room ${room.id}`}</span>
                </div>
              </AccordionHeader>
              <AccordionBody accordionId={roomIdStr}>
                {hasImages ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {images.map((image, index) => (
                      <Card
                        key={index}
                        style={{ cursor: "pointer", margin: 0, backgroundColor: "white" }}
                        className="p-2"
                        onClick={() => window.open(addTimestampToUrl(image.url || image.image_url), '_blank')}
                      >
                        <CardImg
                          top
                          src={addTimestampToUrl(image.url || image.image_url)}
                          alt={image.image_type || "Image"}
                          style={{ maxHeight: "200px", objectFit: "contain" }}
                        />
                        <CardBody className="p-2 text-center">
                          <small className="text-muted fw-medium">
                            {formatImageType(image.image_type)}
                          </small>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="fa-solid fa-image fa-2x mb-2" />
                    <p className="mb-0">No images captured yet</p>
                  </div>
                )}
              </AccordionBody>
            </AccordionItem>
          );
        })}
      </Accordion>

      {rooms.length === 0 && (
        <div className="text-center text-muted py-5">
          <p>No rooms found in this project</p>
        </div>
      )}

      {/* Captured Images Modal */}
      <CapturedImages
        isOpen={showPrintModal}
        toggle={() => setShowPrintModal(false)}
        roomImages={roomImages}
        rooms={rooms}
      />
    </div>
  );
};

export default RoomRendersPane;
